'use client';

import React, { useState } from 'react';

// ─── TYPES ────────────────────────────────────────────────────────────────────

type YaqeenLevel = 'Ilm' | 'Ayn' | 'Haqq';
type ObservationDomain = 'structural' | 'soil' | 'research' | 'ecological' | 'social' | 'spiritual';
type ReviewStatus = 'pending' | 'in-review' | 'verified' | 'co-authored';
type VocationalLens = 'engineer' | 'teacher' | 'farmer' | 'scientist' | 'returning-citizen' | 'custodian';

interface IsnadLink {
  name: string;
  role: string;
  yaqeen: YaqeenLevel;
  action: string;
  timestamp: string;
}

interface VocationalObservation {
  id: string;
  submitter: string;
  vocation: string;
  vocationalIcon: string;
  domain: ObservationDomain;
  yaqeenLevel: YaqeenLevel;
  title: string;
  finding: string;
  namesExtracted: string[];
  ward: string;
  questSource: string;
  isnadChain: IsnadLink[];
  reviewers: { name: string; role: string; status: 'assigned' | 'reviewing' | 'approved' }[];
  status: ReviewStatus;
  mrgBridge: string;
  color: string;
}

interface SynthesisInsight {
  id: string;
  title: string;
  wardLevel: string;
  contributingObservations: string[];
  mizanReading: string;
  namesActive: string[];
  mrgEntry: string;
  coAuthors: { name: string; vocation: string }[];
  exportReady: boolean;
}

// ─── MOCK DATA ────────────────────────────────────────────────────────────────

const VOCATIONAL_OBSERVATIONS: VocationalObservation[] = [
  {
    id: 'obs-eng-01',
    submitter: 'Khalid A.',
    vocation: 'Engineer',
    vocationalIcon: '⚙',
    domain: 'structural',
    yaqeenLevel: 'Ayn',
    title: 'Water Infrastructure Decay — Vine City Ward',
    finding: 'Aging pipe infrastructure in Vine City shows 40% capacity reduction. Al-Razzaq provision flow is structurally blocked at the material layer. The Taqdir measurement of this ward\'s water access reflects a decree that calls for caretaker intervention.',
    namesExtracted: ['Al-Razzaq', 'Al-Qadir', 'Al-Khaliq'],
    ward: 'Vine City / Eng',
    questSource: 'Systems Decomposition Quest — Day 14',
    isnadChain: [
      { name: 'Khalid A.', role: 'Engineer', yaqeen: 'Ayn', action: 'Field observation + structural analysis', timestamp: '2026-03-12' },
      { name: 'Amira T.', role: 'Scientist', yaqeen: 'Ilm', action: 'Cross-referenced with water quality data', timestamp: '2026-03-13' },
      { name: 'Ustadh Yusuf', role: 'Scholar Reviewer', yaqeen: 'Haqq', action: 'Verified Names extraction and Quranic grounding', timestamp: '2026-03-14' },
    ],
    reviewers: [
      { name: 'Amira T.', role: 'Scientist', status: 'approved' },
      { name: 'Ustadh Yusuf', role: 'Scholar', status: 'approved' },
      { name: 'Fatima R.', role: 'Community Planner', status: 'reviewing' },
    ],
    status: 'verified',
    mrgBridge: 'MRG Ward Infrastructure · Provision Access Map',
    color: '#2d8a87',
  },
  {
    id: 'obs-farm-01',
    submitter: 'Dawud M.',
    vocation: 'Farmer',
    vocationalIcon: '⬡',
    domain: 'soil',
    yaqeenLevel: 'Haqq',
    title: 'Soil Compaction Patterns — Westside Community Garden',
    finding: 'Three seasons of observation reveal soil compaction correlates with food desert boundaries. The Rain Hadith is manifest: same rain, different soils. The Taqdir of this soil is a measure of historical disinvestment. Al-Muhyyi\'s life-giving capacity is present but blocked by compaction.',
    namesExtracted: ['Al-Muhyyi', 'Al-Razzaq', 'Al-Qadir', 'Al-Khaliq'],
    ward: 'Westside',
    questSource: 'Soil Reading Quest — Seasonal Cycle 3',
    isnadChain: [
      { name: 'Dawud M.', role: 'Farmer', yaqeen: 'Haqq', action: 'Three-season soil observation and documentation', timestamp: '2026-02-28' },
      { name: 'Khalid A.', role: 'Engineer', yaqeen: 'Ayn', action: 'Structural analysis of compaction causes', timestamp: '2026-03-05' },
      { name: 'Dr. Layla S.', role: 'Scientist', yaqeen: 'Ayn', action: 'Lab analysis of soil composition', timestamp: '2026-03-08' },
      { name: 'Ustadh Yusuf', role: 'Scholar Reviewer', yaqeen: 'Haqq', action: 'Hadith grounding verified — Rain Hadith application', timestamp: '2026-03-10' },
    ],
    reviewers: [
      { name: 'Khalid A.', role: 'Engineer', status: 'approved' },
      { name: 'Dr. Layla S.', role: 'Scientist', status: 'approved' },
      { name: 'Ustadh Yusuf', role: 'Scholar', status: 'approved' },
    ],
    status: 'co-authored',
    mrgBridge: 'MRG Food Access · Soil Health Index',
    color: '#2d7a47',
  },
  {
    id: 'obs-sci-01',
    submitter: 'Dr. Layla S.',
    vocation: 'Scientist',
    vocationalIcon: '◎',
    domain: 'research',
    yaqeenLevel: 'Ayn',
    title: 'Photosynthetic Efficiency Variance — Urban Canopy Study',
    finding: 'Urban tree canopy in food desert zones shows 60% lower photosynthetic efficiency than adjacent affluent zones. Al-Nur\'s light reaches both zones equally — the Taqdir difference is in the soil and air quality. Sunnat Allah operates identically; human choices have altered the receiving conditions.',
    namesExtracted: ['Al-Nur', 'Al-Khaliq', 'Al-Muhyyi', 'Al-Alim'],
    ward: 'Vine City / Eng / Westside',
    questSource: 'Observation Chain Archive — Research Track',
    isnadChain: [
      { name: 'Dr. Layla S.', role: 'Scientist', yaqeen: 'Ayn', action: 'Multi-site photosynthesis measurement study', timestamp: '2026-03-01' },
      { name: 'Dawud M.', role: 'Farmer', yaqeen: 'Haqq', action: 'Ground-truth soil correlation confirmed', timestamp: '2026-03-06' },
      { name: 'Khalid A.', role: 'Engineer', yaqeen: 'Ayn', action: 'Infrastructure impact analysis added', timestamp: '2026-03-09' },
    ],
    reviewers: [
      { name: 'Dawud M.', role: 'Farmer', status: 'approved' },
      { name: 'Khalid A.', role: 'Engineer', status: 'approved' },
      { name: 'Fatima R.', role: 'Urban Planner', status: 'assigned' },
    ],
    status: 'in-review',
    mrgBridge: 'MRG Environmental Health · Canopy Coverage Map',
    color: '#8a8ab8',
  },
];

const SYNTHESIS_INSIGHTS: SynthesisInsight[] = [
  {
    id: 'syn-01',
    title: 'Vine City / Westside Ward — Mizan Disruption Pattern',
    wardLevel: 'Vine City · Eng · Westside',
    contributingObservations: ['obs-eng-01', 'obs-farm-01', 'obs-sci-01'],
    mizanReading: 'Three vocational lenses converge on a single Taqdir pattern: Al-Razzaq\'s provision is present (rain, light, soil capacity) but structurally blocked at every layer — infrastructure, soil compaction, and canopy efficiency. The Mizan is disrupted not by absence of divine provision but by accumulated human choices that have altered the receiving conditions. The Rain Hadith is the theological proof text: same rain, different soils. The Amanah of caretakership calls for intervention at all three layers simultaneously.',
    namesActive: ['Al-Razzaq', 'Al-Qadir', 'Al-Muhyyi', 'Al-Nur', 'Al-Khaliq', 'Al-Alim'],
    mrgEntry: 'MRG Co-Authored Entry #2026-03-14 · Ward: Vine City/Eng/Westside · Mizan Disruption Pattern · Three-Vocation Synthesis',
    coAuthors: [
      { name: 'Khalid A.', vocation: 'Engineer' },
      { name: 'Dawud M.', vocation: 'Farmer' },
      { name: 'Dr. Layla S.', vocation: 'Scientist' },
    ],
    exportReady: true,
  },
];

// ─── YAQEEN BADGE ─────────────────────────────────────────────────────────────

function YaqeenBadge({ level }: { level: YaqeenLevel }) {
  const config = {
    Ilm: { bg: 'rgba(45,122,71,0.12)', border: 'rgba(45,122,71,0.3)', text: '#2d7a47', arabic: 'عِلْم' },
    Ayn: { bg: 'rgba(45,138,135,0.12)', border: 'rgba(45,138,135,0.3)', text: '#2d8a87', arabic: 'عَيْن' },
    Haqq: { bg: 'rgba(201,168,76,0.12)', border: 'rgba(201,168,76,0.3)', text: '#c9a84c', arabic: 'حَقّ' },
  }[level];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: config.bg, border: `1px solid ${config.border}`,
      borderRadius: 3, padding: '2px 7px',
      fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
      textTransform: 'uppercase', color: config.text,
    }}>
      {level} <span style={{ fontFamily: "'Amiri', serif", fontSize: 10 }}>{config.arabic}</span>
    </span>
  );
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ReviewStatus }) {
  const config = {
    pending: { bg: 'rgba(212,197,169,0.08)', border: 'rgba(212,197,169,0.2)', text: 'rgba(212,197,169,0.5)', label: 'Pending' },
    'in-review': { bg: 'rgba(45,138,135,0.1)', border: 'rgba(45,138,135,0.25)', text: '#2d8a87', label: 'In Review' },
    verified: { bg: 'rgba(45,122,71,0.1)', border: 'rgba(45,122,71,0.25)', text: '#2d7a47', label: 'Verified' },
    'co-authored': { bg: 'rgba(201,168,76,0.1)', border: 'rgba(201,168,76,0.25)', text: '#c9a84c', label: 'Co-Authored' },
  }[status];
  return (
    <span style={{
      display: 'inline-block',
      background: config.bg, border: `1px solid ${config.border}`,
      borderRadius: 3, padding: '2px 8px',
      fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em',
      textTransform: 'uppercase', color: config.text,
    }}>
      {config.label}
    </span>
  );
}

// ─── ISNAD TRAIL ──────────────────────────────────────────────────────────────

function IsnadTrail({ chain, reviewers, status }: {
  chain: IsnadLink[];
  reviewers: VocationalObservation['reviewers'];
  status: ReviewStatus;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 3, padding: '5px 10px', cursor: 'pointer',
          fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
          textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)',
        }}
      >
        <span>✦ Isnad Verification Trail</span>
        <span style={{ marginLeft: 4, fontSize: 7 }}>{expanded ? '▲' : '▼'}</span>
        <span style={{ marginLeft: 'auto', fontSize: 7, color: 'rgba(212,197,169,0.4)' }}>
          {chain.length} links · {reviewers.filter(r => r.status === 'approved').length}/{reviewers.length} reviewers
        </span>
      </button>

      {expanded && (
        <div style={{ marginTop: 8, padding: '12px 14px', background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 4 }}>
          {/* Chain */}
          <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 10 }}>
            Transmission Chain
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {chain.map((link, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {/* Connector */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontFamily: 'monospace', fontSize: 8, color: '#c9a84c',
                  }}>{i + 1}</div>
                  {i < chain.length - 1 && (
                    <div style={{ width: 1, height: 20, background: 'rgba(201,168,76,0.15)', margin: '2px 0' }} />
                  )}
                </div>
                {/* Content */}
                <div style={{ flex: 1, paddingBottom: i < chain.length - 1 ? 4 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#d4c5a9', fontWeight: 700 }}>{link.name}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.45)' }}>·</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{link.role}</span>
                    <YaqeenBadge level={link.yaqeen} />
                    <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.3)', marginLeft: 'auto' }}>{link.timestamp}</span>
                  </div>
                  <p style={{ fontSize: 11, color: 'rgba(212,197,169,0.55)', lineHeight: 1.5, margin: 0 }}>{link.action}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Reviewers */}
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: '1px solid rgba(201,168,76,0.08)' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>
              Role-Specific Reviewers (Auto-Assigned by Domain)
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {reviewers.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 8px', borderRadius: 3,
                  background: r.status === 'approved' ? 'rgba(45,122,71,0.1)' : r.status === 'reviewing' ? 'rgba(45,138,135,0.1)' : 'rgba(212,197,169,0.05)',
                  border: `1px solid ${r.status === 'approved' ? 'rgba(45,122,71,0.25)' : r.status === 'reviewing' ? 'rgba(45,138,135,0.2)' : 'rgba(212,197,169,0.1)'}`,
                }}>
                  <span style={{ fontSize: 8, color: r.status === 'approved' ? '#2d7a47' : r.status === 'reviewing' ? '#2d8a87' : 'rgba(212,197,169,0.3)' }}>
                    {r.status === 'approved' ? '✓' : r.status === 'reviewing' ? '◎' : '○'}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.6)' }}>{r.name}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)', textTransform: 'uppercase', letterSpacing: '.06em' }}>{r.role}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status */}
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)' }}>Verification Status:</span>
            <StatusBadge status={status} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OBSERVATION CARD ─────────────────────────────────────────────────────────

function ObservationCard({ obs }: { obs: VocationalObservation }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div style={{
      background: `${obs.color}06`,
      border: `1px solid ${obs.color}20`,
      borderLeft: `3px solid ${obs.color}`,
      borderRadius: 6,
      padding: '16px 18px',
      marginBottom: 12,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
          background: `${obs.color}15`, border: `1px solid ${obs.color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, color: obs.color,
        }}>{obs.vocationalIcon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 9, fontWeight: 700, color: obs.color, textTransform: 'uppercase', letterSpacing: '.08em' }}>{obs.vocation}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>·</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)' }}>{obs.submitter}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>·</span>
            <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)' }}>{obs.ward}</span>
            <YaqeenBadge level={obs.yaqeenLevel} />
            <StatusBadge status={obs.status} />
          </div>
          <h4 style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: '#d4c5a9', margin: 0, lineHeight: 1.4 }}>{obs.title}</h4>
        </div>
      </div>

      {/* Quest source */}
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)' }}>Quest Source: </span>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.6)', fontStyle: 'italic' }}>{obs.questSource}</span>
      </div>

      {/* Finding */}
      <p style={{ fontSize: 13, color: 'rgba(212,197,169,0.7)', lineHeight: 1.75, marginBottom: 10 }}>{obs.finding}</p>

      {/* Names extracted */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)', alignSelf: 'center' }}>Names:</span>
        {obs.namesExtracted.map(n => (
          <span key={n} style={{
            fontFamily: 'monospace', fontSize: 8, padding: '2px 6px', borderRadius: 2,
            background: 'rgba(138,138,184,0.1)', border: '1px solid rgba(138,138,184,0.2)',
            color: '#8a8ab8',
          }}>{n}</span>
        ))}
      </div>

      {/* MRG bridge */}
      <div style={{ marginBottom: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)' }}>MRG Bridge: </span>
        <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(45,122,71,0.7)' }}>{obs.mrgBridge}</span>
      </div>

      {/* Isnad Trail */}
      <IsnadTrail chain={obs.isnadChain} reviewers={obs.reviewers} status={obs.status} />
    </div>
  );
}

// ─── SYNTHESIS PANEL ──────────────────────────────────────────────────────────

function SynthesisPanel({ insight, observations }: { insight: SynthesisInsight; observations: VocationalObservation[] }) {
  const [exported, setExported] = useState(false);
  const contributing = observations.filter(o => insight.contributingObservations.includes(o.id));

  return (
    <div style={{
      background: 'rgba(201,168,76,0.04)',
      border: '1px solid rgba(201,168,76,0.2)',
      borderRadius: 8,
      padding: '24px',
      marginBottom: 24,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 6 }}>
            ✦ Ward-Level Mizan Synthesis
          </p>
          <h3 style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.04em', color: '#F5F0E8', margin: 0, lineHeight: 1.3 }}>
            {insight.title}
          </h3>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', marginTop: 4 }}>{insight.wardLevel}</p>
        </div>
        {insight.exportReady && (
          <button
            onClick={() => setExported(true)}
            style={{
              flexShrink: 0,
              background: exported ? 'rgba(45,122,71,0.15)' : 'rgba(201,168,76,0.1)',
              border: `1px solid ${exported ? 'rgba(45,122,71,0.4)' : 'rgba(201,168,76,0.3)'}`,
              borderRadius: 4, padding: '8px 14px', cursor: 'pointer',
              fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em',
              textTransform: 'uppercase', color: exported ? '#2d7a47' : '#c9a84c',
              transition: 'all 0.2s',
            }}
          >
            {exported ? '✓ Exported to MRG' : '⬡ Export to MRG'}
          </button>
        )}
      </div>

      {/* Vocation chain visualization */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16, flexWrap: 'wrap', rowGap: 8 }}>
        {contributing.map((obs, i) => (
          <React.Fragment key={obs.id}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 12px', borderRadius: 4,
              background: `${obs.color}10`, border: `1px solid ${obs.color}25`,
            }}>
              <span style={{ fontSize: 12, color: obs.color }}>{obs.vocationalIcon}</span>
              <div>
                <p style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, color: obs.color, textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>{obs.vocation}</p>
                <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', margin: 0 }}>{obs.submitter}</p>
              </div>
            </div>
            {i < contributing.length - 1 && (
              <div style={{ padding: '0 6px', color: 'rgba(201,168,76,0.3)', fontSize: 12 }}>→</div>
            )}
          </React.Fragment>
        ))}
        <div style={{ padding: '0 6px', color: 'rgba(201,168,76,0.3)', fontSize: 12 }}>→</div>
        <div style={{
          padding: '6px 12px', borderRadius: 4,
          background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)',
        }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '.06em', margin: 0 }}>Mizan Insight</p>
          <p style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.4)', margin: 0 }}>Ward Level</p>
        </div>
      </div>

      {/* Mizan reading */}
      <div style={{ background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 6, padding: '16px 18px', marginBottom: 14 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.4)', marginBottom: 8 }}>Mizan Reading</p>
        <p style={{ fontSize: 13, color: 'rgba(212,197,169,0.75)', lineHeight: 1.8, margin: 0 }}>{insight.mizanReading}</p>
      </div>

      {/* Names active */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 14 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)', alignSelf: 'center' }}>Names Active:</span>
        {insight.namesActive.map(n => (
          <span key={n} style={{
            fontFamily: 'monospace', fontSize: 8, padding: '2px 7px', borderRadius: 2,
            background: 'rgba(138,138,184,0.12)', border: '1px solid rgba(138,138,184,0.25)',
            color: '#8a8ab8',
          }}>{n}</span>
        ))}
      </div>

      {/* MRG entry */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.15)', borderRadius: 4 }}>
        <span style={{ fontSize: 10, color: '#2d7a47' }}>⬡</span>
        <div>
          <p style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', margin: 0, marginBottom: 2 }}>MRG Co-Authored Entry</p>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(45,122,71,0.8)', margin: 0 }}>{insight.mrgEntry}</p>
        </div>
      </div>

      {/* Co-authors */}
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,197,169,0.35)' }}>Co-Authors:</span>
        {insight.coAuthors.map((a, i) => (
          <span key={i} style={{
            fontFamily: 'monospace', fontSize: 8, padding: '2px 8px', borderRadius: 2,
            background: 'rgba(212,197,169,0.06)', border: '1px solid rgba(212,197,169,0.12)',
            color: 'rgba(212,197,169,0.6)',
          }}>{a.name} <span style={{ color: 'rgba(212,197,169,0.35)' }}>({a.vocation})</span></span>
        ))}
      </div>
    </div>
  );
}

// ─── VOCATIONAL LENS CONFIG ───────────────────────────────────────────────────

const LENS_CONFIG: Record<VocationalLens, {
  label: string;
  icon: string;
  color: string;
  amanahDomain: string;
  promptVariant: string;
  defaultNames: string[];
  defaultYaqeen: YaqeenLevel;
}> = {
  engineer: {
    label: 'Engineer',
    icon: '⚙',
    color: '#2d8a87',
    amanahDomain: 'Infrastructure & Systems',
    promptVariant: 'Analyze the structural or systemic dimension of what you observed. What load-bearing relationships are present? What is the Taqdir measurement of this system\'s capacity? Which Names of Allah are manifest in its design?',
    defaultNames: ['Al-Qadir', 'Al-Razzaq', 'Al-Khaliq'],
    defaultYaqeen: 'Ayn',
  },
  teacher: {
    label: 'Teacher',
    icon: '◈',
    color: '#8a8ab8',
    amanahDomain: 'Knowledge Transmission',
    promptVariant: 'What did you observe that could become a lesson? How does this observation connect to the Rain Hadith — different soils, same rain? What curriculum moment does this create?',
    defaultNames: ['Al-Alim', 'Al-Hakim', 'Al-Khabir'],
    defaultYaqeen: 'Ilm',
  },
  farmer: {
    label: 'Farmer',
    icon: '⬡',
    color: '#2d7a47',
    amanahDomain: 'Soil & Seasonal Cycles',
    promptVariant: 'What did the soil, season, or plant tell you? How does this observation reflect the Mizan — the balance? What is the Taqdir of this land\'s capacity right now?',
    defaultNames: ['Al-Muhyyi', 'Al-Razzaq', 'Al-Qadir'],
    defaultYaqeen: 'Haqq',
  },
  scientist: {
    label: 'Scientist',
    icon: '◎',
    color: '#c9a84c',
    amanahDomain: 'Research & Evidence',
    promptVariant: 'What measurable pattern did you observe? How does this data point connect to a Name or Attribute of Allah? What does the Sunnat Allah — the Way of Allah — look like in this finding?',
    defaultNames: ['Al-Alim', 'Al-Nur', 'Al-Khaliq'],
    defaultYaqeen: 'Ayn',
  },
  'returning-citizen': {
    label: 'Returning Citizen',
    icon: '◇',
    color: '#c97a4c',
    amanahDomain: 'Mizan Restoration',
    promptVariant: 'What barrier to Mizan did you observe in your locality? What reform or redistribution would restore balance? What is the Islah — the mending — that this situation calls for?',
    defaultNames: ['Al-Adl', 'Al-Haqq', 'Al-Qadir'],
    defaultYaqeen: 'Ilm',
  },
  custodian: {
    label: 'Custodian',
    icon: '✦',
    color: '#7a8ab8',
    amanahDomain: 'Habitat & Stewardship',
    promptVariant: 'What did you tend, clean, or restore? What was the state of this space before and after your custodianship? Which Names of Allah are honored by this act of care?',
    defaultNames: ['Al-Muhyyi', 'Al-Khaliq', 'Al-Qadir'],
    defaultYaqeen: 'Ilm',
  },
};

const ALL_NAMES = [
  'Al-Qadir', 'Al-Alim', 'Al-Hakim', 'Al-Razzaq', 'Al-Khaliq', 'Al-Muhyyi',
  'Al-Mumit', 'Al-Nur', 'Al-Haqq', 'Al-Adl', 'Al-Khabir', 'Al-Latif',
  'Al-Baseer', 'Al-Badi', 'Al-Ghafur', 'Ar-Rahim', 'Ar-Rahman', 'Al-Wadud',
];

const AMANAH_DOMAINS = [
  'Infrastructure & Systems', 'Knowledge Transmission', 'Soil & Seasonal Cycles',
  'Research & Evidence', 'Mizan Restoration', 'Habitat & Stewardship',
  'Community Mutual Aid', 'Urban Planning', 'Spiritual Formation', 'Cultural Production',
];

// ─── OBSERVATION FORM ─────────────────────────────────────────────────────────

function ObservationForm({ linkedToSynthesis }: { linkedToSynthesis: boolean }) {
  const [activeLens, setActiveLens] = useState<VocationalLens>('engineer');
  const [yaqeenLevel, setYaqeenLevel] = useState<YaqeenLevel>('Ilm');
  const [namesToggleOpen, setNamesToggleOpen] = useState(false);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [amanahDomain, setAmanahDomain] = useState('');
  const [questSource, setQuestSource] = useState('');
  const [observationText, setObservationText] = useState('');
  const [ward, setWard] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const lens = LENS_CONFIG[activeLens];

  // Auto-populate fields when lens changes
  const handleLensChange = (l: VocationalLens) => {
    setActiveLens(l);
    setSelectedNames(LENS_CONFIG[l].defaultNames);
    setYaqeenLevel(LENS_CONFIG[l].defaultYaqeen);
    setAmanahDomain(LENS_CONFIG[l].amanahDomain);
  };

  const toggleName = (name: string) => {
    setSelectedNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const timestamp = new Date().toISOString().split('T')[0];

  if (submitted) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center', background: 'rgba(45,122,71,0.06)', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 8 }}>
        <p style={{ fontSize: 20, marginBottom: 8 }}>✓</p>
        <p className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: '#2d7a47' }}>Observation Submitted</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.65, marginBottom: 16 }}>
          Your observation has been added to the Isnad chain. Role-specific reviewers have been auto-assigned based on your Amanah domain: <strong style={{ color: 'rgba(212,197,169,0.8)' }}>{amanahDomain}</strong>.
        </p>
        {linkedToSynthesis && (
          <p style={{ color: 'rgba(201,168,76,0.6)', fontSize: 12, fontStyle: 'italic' }}>
            ✦ Linked to Synthesis Panel — will surface in ward-level Mizan reading when 2+ vocations submit from the same ward.
          </p>
        )}
        <button
          onClick={() => { setSubmitted(false); setObservationText(''); setQuestSource(''); setWard(''); }}
          style={{ marginTop: 16, background: 'transparent', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 4, padding: '8px 16px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: '#2d7a47' }}
        >
          Submit Another
        </button>
      </div>
    );
  }

  return (
    <div style={{ background: 'rgba(6,13,18,0.8)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 8, padding: '24px' }}>
      <p className="font-mono text-[9px] uppercase tracking-widest mb-4" style={{ color: 'rgba(201,197,169,0.4)' }}>
        ◈ Observation Form — Full Isnad Context
      </p>

      {/* ── VOCATIONAL LENS SELECTOR ── */}
      <div style={{ marginBottom: 20 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Active Vocational Lens</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(Object.entries(LENS_CONFIG) as [VocationalLens, typeof LENS_CONFIG[VocationalLens]][]).map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => handleLensChange(key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
                background: activeLens === key ? `${cfg.color}15` : 'transparent',
                border: `1px solid ${activeLens === key ? cfg.color : 'rgba(255,255,255,0.08)'}`,
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase',
                color: activeLens === key ? cfg.color : 'rgba(212,197,169,0.4)',
                transition: 'all 0.15s',
              }}
            >
              <span>{cfg.icon}</span> {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── ROLE-SPECIFIC PROMPT VARIANT ── */}
      <div style={{ marginBottom: 20, padding: '14px 16px', background: `${lens.color}08`, border: `1px solid ${lens.color}20`, borderRadius: 6 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: `${lens.color}80` }}>
          {lens.icon} {lens.label} Prompt Variant
        </p>
        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>
          {lens.promptVariant}
        </p>
      </div>

      {/* ── OBSERVATION TEXT ── */}
      <div style={{ marginBottom: 16 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Your Observation</p>
        <textarea
          value={observationText}
          onChange={e => setObservationText(e.target.value)}
          placeholder="Describe what you observed through your vocational lens..."
          rows={4}
          style={{
            width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 4, padding: '10px 12px', color: 'rgba(212,197,169,0.8)', fontSize: 13,
            lineHeight: 1.7, resize: 'vertical', fontFamily: 'inherit', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ── YAQEEN LEVEL SELECTOR ── */}
      <div style={{ marginBottom: 16 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Yaqeen Level — Certainty of Observation</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {([
            { level: 'Ilm' as YaqeenLevel, arabic: 'عِلْم', desc: 'Knowledge — heard or read', color: '#2d7a47' },
            { level: 'Ayn' as YaqeenLevel, arabic: 'عَيْن', desc: 'Eye — directly witnessed', color: '#2d8a87' },
            { level: 'Haqq' as YaqeenLevel, arabic: 'حَقّ', desc: 'Reality — lived and embodied', color: '#c9a84c' },
          ]).map(y => (
            <button
              key={y.level}
              onClick={() => setYaqeenLevel(y.level)}
              style={{
                flex: 1, minWidth: 100, padding: '10px 12px', borderRadius: 4, cursor: 'pointer',
                background: yaqeenLevel === y.level ? `${y.color}15` : 'transparent',
                border: `1px solid ${yaqeenLevel === y.level ? y.color : 'rgba(255,255,255,0.08)'}`,
                textAlign: 'left', transition: 'all 0.15s',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span className="font-mono text-[10px] font-black uppercase" style={{ color: yaqeenLevel === y.level ? y.color : 'rgba(212,197,169,0.5)' }}>{y.level}</span>
                <span style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: yaqeenLevel === y.level ? `${y.color}80` : 'rgba(212,197,169,0.3)' }}>{y.arabic}</span>
              </div>
              <p style={{ fontFamily: 'monospace', fontSize: 8, color: yaqeenLevel === y.level ? 'rgba(212,197,169,0.6)' : 'rgba(212,197,169,0.3)', margin: 0 }}>{y.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* ── NAMES EXTRACTION QUICK-TOGGLE ── */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <p className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(212,197,169,0.4)' }}>Names Extracted</p>
          <button
            onClick={() => setNamesToggleOpen(!namesToggleOpen)}
            style={{ background: 'none', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 3, padding: '3px 8px', cursor: 'pointer', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.6)' }}
          >
            {namesToggleOpen ? '▲ Collapse' : '▼ Expand All Names'}
          </button>
        </div>
        {/* Pre-selected by lens */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: namesToggleOpen ? 10 : 0 }}>
          {(namesToggleOpen ? ALL_NAMES : lens.defaultNames).map(name => (
            <button
              key={name}
              onClick={() => toggleName(name)}
              style={{
                padding: '3px 8px', borderRadius: 3, cursor: 'pointer',
                background: selectedNames.includes(name) ? 'rgba(138,138,184,0.15)' : 'rgba(138,138,184,0.04)',
                border: `1px solid ${selectedNames.includes(name) ? 'rgba(138,138,184,0.4)' : 'rgba(138,138,184,0.15)'}`,
                fontFamily: 'monospace', fontSize: 9,
                color: selectedNames.includes(name) ? '#8a8ab8' : 'rgba(138,138,184,0.4)',
                transition: 'all 0.1s',
              }}
            >
              {selectedNames.includes(name) ? '✓ ' : ''}{name}
            </button>
          ))}
        </div>
        {selectedNames.length > 0 && (
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(138,138,184,0.5)', marginTop: 4 }}>
            {selectedNames.length} name{selectedNames.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* ── AMANAH DOMAIN ── */}
      <div style={{ marginBottom: 16 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Amanah Domain</p>
        <select
          value={amanahDomain}
          onChange={e => setAmanahDomain(e.target.value)}
          style={{
            width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 4, padding: '8px 12px', color: amanahDomain ? 'rgba(212,197,169,0.8)' : 'rgba(212,197,169,0.35)',
            fontSize: 13, fontFamily: 'inherit', outline: 'none', cursor: 'pointer',
          }}
        >
          <option value="" disabled>Select Amanah domain...</option>
          {AMANAH_DOMAINS.map(d => <option key={d} value={d} style={{ background: '#060d12' }}>{d}</option>)}
        </select>
      </div>

      {/* ── QUEST SOURCE REFERENCE ── */}
      <div style={{ marginBottom: 16 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Quest Source Reference</p>
        <input
          type="text"
          value={questSource}
          onChange={e => setQuestSource(e.target.value)}
          placeholder="e.g. Systems Decomposition Quest — Day 14"
          style={{
            width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 4, padding: '8px 12px', color: 'rgba(212,197,169,0.8)', fontSize: 13,
            fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ── WARD / LOCALITY ── */}
      <div style={{ marginBottom: 16 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.4)' }}>Ward / Locality</p>
        <input
          type="text"
          value={ward}
          onChange={e => setWard(e.target.value)}
          placeholder="e.g. Vine City, Westside, Ward 3..."
          style={{
            width: '100%', background: 'rgba(6,13,18,0.6)', border: '1px solid rgba(201,168,76,0.15)',
            borderRadius: 4, padding: '8px 12px', color: 'rgba(212,197,169,0.8)', fontSize: 13,
            fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* ── TIMESTAMP CHAIN PREVIEW ── */}
      <div style={{ marginBottom: 20, padding: '12px 14px', background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.1)', borderRadius: 6 }}>
        <p className="font-mono text-[8px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>Isnad Timestamp Chain Preview</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[
            { step: '1', label: 'Submission', actor: 'You', role: lens.label, ts: timestamp, color: lens.color },
            { step: '2', label: 'Domain Review', actor: 'Auto-assigned reviewer', role: `${amanahDomain || lens.amanahDomain} specialist`, ts: 'Pending', color: 'rgba(201,197,169,0.3)' },
            { step: '3', label: 'Scholar Verification', actor: 'Scholar Reviewer', role: 'Quranic grounding check', ts: 'Pending', color: 'rgba(212,197,169,0.3)' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: `${s.color}20`, border: `1px solid ${s.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'monospace', fontSize: 7, color: s.color }}>{s.step}</div>
                {i < 2 && <div style={{ width: 1, height: 14, background: 'rgba(201,168,76,0.1)', margin: '2px 0' }} />}
              </div>
              <div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 8, fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: '.06em' }}>{s.label}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 7, color: 'rgba(212,197,169,0.35)' }}>{s.ts}</span>
                </div>
                <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.45)', margin: 0 }}>{s.actor} · {s.role}</p>
              </div>
            </div>
          ))}
        </div>
        {linkedToSynthesis && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(201,168,76,0.08)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 9, color: '#c9a84c' }}>✦</span>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(201,168,76,0.5)', margin: 0 }}>Linked to Synthesis Panel — ward-level Mizan aggregation active</p>
          </div>
        )}
      </div>

      {/* ── SUBMIT ── */}
      <button
        onClick={() => {
          if (observationText.trim()) setSubmitted(true);
        }}
        style={{
          width: '100%', padding: '12px 20px', borderRadius: 4, cursor: 'pointer',
          background: observationText.trim() ? `${lens.color}15` : 'rgba(212,197,169,0.04)',
          border: `1px solid ${observationText.trim() ? lens.color : 'rgba(212,197,169,0.1)'}`,
          fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase',
          color: observationText.trim() ? lens.color : 'rgba(212,197,169,0.25)',
          transition: 'all 0.2s',
        }}
      >
        {lens.icon} Submit Observation to Isnad Chain
      </button>
    </div>
  );
}

// ─── MAIN EXPORT ──────────────────────────────────────────────────────────────

export default function SynthesisPanelIsnad() {
  const [activeTab, setActiveTab] = useState<'synthesis' | 'observations' | 'submit'>('synthesis');

  return (
    <section id="section-synthesis-isnad" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
      <div className="max-w-[900px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(139,105,20,0.6)', marginBottom: 12 }}>
            ◈ Vocational Synthesis · Isnad Verification
          </p>
          <h2 style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '.02em', color: '#F5F0E8', marginBottom: 8, lineHeight: 1.2 }}>
            Cross-Vocation Mizan Synthesis
          </h2>
          <p style={{ fontSize: 14, color: 'rgba(212,197,169,0.55)', lineHeight: 1.75, maxWidth: 620 }}>
            When Engineer structural findings, Farmer soil observations, and Scientist research converge on the same ward, a combined Mizan insight emerges — exportable as a co-authored MRG entry. Each observation carries a full Isnad verification trail: submitter, vocation, Yaqeen level, role-specific reviewers auto-assigned by domain, and verification status.
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { id: 'synthesis', label: '✦ Synthesis Panel', count: SYNTHESIS_INSIGHTS.length },
            { id: 'observations', label: '◎ Observations + Isnad', count: VOCATIONAL_OBSERVATIONS.length },
            { id: 'submit', label: '◈ Submit Observation', count: null },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              style={{
                background: activeTab === tab.id ? 'rgba(201,168,76,0.1)' : 'transparent',
                border: `1px solid ${activeTab === tab.id ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 4, padding: '8px 16px', cursor: 'pointer',
                fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em',
                textTransform: 'uppercase',
                color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.4)',
                transition: 'all 0.15s',
              }}
            >
              {tab.label}
              {tab.count !== null && (
                <span style={{
                  marginLeft: 6, padding: '1px 5px', borderRadius: 2,
                  background: activeTab === tab.id ? 'rgba(201,168,76,0.15)' : 'rgba(212,197,169,0.06)',
                  fontSize: 8, color: activeTab === tab.id ? '#c9a84c' : 'rgba(212,197,169,0.3)',
                }}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Synthesis Panel */}
        {activeTab === 'synthesis' && (
          <div>
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 4 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,138,135,0.6)', marginBottom: 4 }}>
                How Synthesis Works
              </p>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6, margin: 0 }}>
                When 2+ vocational observations from the same ward reach Verified status, the system surfaces a synthesis pattern. The Mizan reading combines all vocational lenses into a single ward-level insight. Co-authors can export directly to the MRG as a joint entry with full Isnad attribution.
              </p>
            </div>
            {SYNTHESIS_INSIGHTS.map(insight => (
              <SynthesisPanel key={insight.id} insight={insight} observations={VOCATIONAL_OBSERVATIONS} />
            ))}
          </div>
        )}

        {/* Observations + Isnad */}
        {activeTab === 'observations' && (
          <div>
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(138,138,184,0.06)', border: '1px solid rgba(138,138,184,0.15)', borderRadius: 4 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.6)', marginBottom: 4 }}>
                Isnad Verification
              </p>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6, margin: 0 }}>
                Each quest-sourced observation carries a full Isnad chain — the transmission trail from submitter through reviewers to verified status. Role-specific reviewers are auto-assigned based on observation domain: structural observations route to Engineers + Scholars; soil observations route to Farmers + Scientists; research observations route to Scientists + Community Planners.
              </p>
            </div>
            {VOCATIONAL_OBSERVATIONS.map(obs => (
              <ObservationCard key={obs.id} obs={obs} />
            ))}
          </div>
        )}

        {/* Submit Observation */}
        {activeTab === 'submit' && (
          <div>
            <div style={{ marginBottom: 16, padding: '10px 14px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)', borderRadius: 4 }}>
              <p style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)', marginBottom: 4 }}>
                Full Isnad Context Form
              </p>
              <p style={{ fontSize: 12, color: 'rgba(212,197,169,0.55)', lineHeight: 1.6, margin: 0 }}>
                Select your active vocational lens and the form auto-populates: role-specific prompt variant, default Names, Amanah domain, and Yaqeen level. All fields feed directly into the Isnad chain and link to the Synthesis Panel for ward-level Mizan aggregation.
              </p>
            </div>
            <ObservationForm linkedToSynthesis={true} />
          </div>
        )}
      </div>
    </section>
  );
}
